<template>
  <div class="signWrap">
    <div class="backBg"></div>
    <div v-if="isLoaded" class="container flex col f-center gap-32">
      <div class="signTop">
        <NuxtLink :to="{name: 'index'}" class="logo">
          <img src="~/assets/images/logo_blue.svg" alt="codepresso logo" />
        </NuxtLink>
      </div>
      <LazySignAuthEmailForm
          :user-email="userEmail"
          :auth-type="signUpAuthEmailFlag"
          @resend="onClickResendEmail"
          @complete="signUpComplete"
      />
      <LazySignCommonFooter />
    </div>
  </div>
</template>

<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useUserService} from '~/composables/service/useUserService';
import {LazySignCommonFooter} from '#components';
import {getCookie, removeCookie, setCookie} from '~~/package/utils/CookieUtil';
import {CookieKeyType} from '~~/package/mapper/common/model/CookieKeyType';
import {useToastStore} from '~/store/toast/toastStore';
import {ToastType} from '~/components/Common/Toast/model/ToastType';
import {SignAuthType} from '~/components/Sign/auth/SignAuthType';
import type {AxiosError} from 'axios';
import {useUserStore} from '~/store/user/userStore';

const { t } = useI18n();
const { $i18n } = useNuxtApp();

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const toastStore = useToastStore();
const userService = useUserService();

const signUpAuthEmailFlag = ref<SignAuthType>(SignAuthType.NONE);
const userEmail = ref<string | undefined>(
    route.query.userEmail as string | undefined,
);
const isLoaded = ref(false);

const RESEND_COOKIE_LIFETIME_SECONDS = 30;

const canResendAuthEmail = (): boolean => {
  return getCookie(CookieKeyType.RESEND_AUTH_MAIL) === undefined;
};

const setResendAuthEmailCookie = () => {
  const now = Date.now();
  const expires = new Date(now + RESEND_COOKIE_LIFETIME_SECONDS * 1000);

  setCookie(CookieKeyType.RESEND_AUTH_MAIL, true, {
    expires,
    maxAge: RESEND_COOKIE_LIFETIME_SECONDS,
  });
};

const sendAuthEmail = async (email: string) => {
  try {
    const isSuccess = await userService.userSendAuthEmail(email);

    if (isSuccess) {
      toastStore.openToast({
        toastType: ToastType.SUCCESS,
        contents: t('sign-resend-verification-email'),
      });
    } else {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t('error-sign-wrong-approach'),
      });
    }
  } catch (error) {
    console.error(error);
    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: t('error-sign-wrong-approach'),
    });
  }
};

const onClickResendEmail = () => {
  if (!userEmail.value) {
    signUpAuthEmailFlag.value = SignAuthType.ERROR;
    return;
  }

  if (canResendAuthEmail()) {
    void sendAuthEmail(userEmail.value);
    setResendAuthEmailCookie();
  } else {
    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: t('sign-send-verification-email-delay'),
    });
  }
};

const userAuthMail = async (userId: number, token: string) => {
  try {
    const accessToken: string = await userService.userAuthToken(userId, token);

    if (accessToken && accessToken.length > 0) {
      // SEC-1 (TSK-10568): backend's /user/auth/email sets the HttpOnly AccessToken cookie.
      setCookie(CookieKeyType.OPEN_CREDIT_BANNER, true);

      await userService.setUser(false);
      await userService.setUserLocale($i18n.locale.value ?? 'en');

      const userData = userService.getUserV2();

      if (userData.id !== 0) {
        userEmail.value = userData.email;
        signUpAuthEmailFlag.value = SignAuthType.COMPLETE;
        return;
      }
    }

    signUpAuthEmailFlag.value = SignAuthType.ERROR;
  } catch (error) {
    console.error(error);

    const axiosError = error as AxiosError<{
      code?: number;
      message?: string;
      data?: string;
    }>;

    const errorData = axiosError.response?.data?.data;

    if (errorData === 'CP_1006') {
      removeCookie(CookieKeyType.RESEND_AUTH_MAIL);
      signUpAuthEmailFlag.value = SignAuthType.EXPIRED;
    } else {
      signUpAuthEmailFlag.value = SignAuthType.ERROR;
    }
  }
};

const initPage = async () => {
  const userIdParam = route.query.userId as string | undefined;
  const tokenParam = route.query.token as string | undefined;

  if (userIdParam && tokenParam) {
    signUpAuthEmailFlag.value = SignAuthType.NONE;
    await userAuthMail(parseInt(userIdParam, 10), tokenParam);
  } else if (userEmail.value) {
    signUpAuthEmailFlag.value = SignAuthType.SEND;
  } else {
    await router.replace('/sign');
  }

  isLoaded.value = true;
};

const signUpComplete = async () => {
  await userService.setUser();
  await router.push({ path: "/register/info" });
};

onMounted(() => {
  void initPage();
});
</script>

<style>
@import '~/assets/scss/form_style.scss';
</style>